import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectCurrentUser, selectIsAuthenticated } from '../../features/auth/authSlice';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import PropertyGrid from './components/PropertyGrid';
import MapView from './components/MapView';
import HeroSlider from './components/HeroSlider';
import CategoriesSlider from './components/CategoriesSlider';
import { useGetSliderQuery, useGetCategoriesQuery, useGetUserDataQuery, useGetUserFavouritePropertyQuery } from '../../features/api/apiSlice';
import usePropertySearch from '../../lib/hooks/usePropertySearch'; // Import usePropertySearch hook

const initialFilters = {
  location: '',
  propertyType: 'all',
  priceMin: '',
  priceMax: '',
  priceRange: [0, 5000000],
  bedrooms: 'any',
  bathrooms: 'any',
  sqftMin: '',
  sqftMax: '',
  sqftRange: [0, 10000],
  yearBuiltRange: [1900, 2025],
  status: 'all',
  sortBy: 'relevance',
  amenities: [],
  features: [],
  recentlyAdded: 'any',
  hasVirtualTour: false,
  distanceFromCity: 50,
  hoaFeeMax: '',
  lotSizeMin: ''
};

const PropertySearchBrowse = () => {
  const navigate = useNavigate();
  
  const user = useSelector(selectCurrentUser);
  const isAuthenticated = useSelector(selectIsAuthenticated);

  const { data: userFavoriteProperties = [] } = useGetUserFavouritePropertyQuery(undefined, {
    skip: !isAuthenticated,
  });

  const favoritePropertyIds = userFavoriteProperties.map(fav => fav.property_id);

  const [selectedProperty, setSelectedProperty] = useState(null);
  
  const {
    properties,
    isLoading,
    isFetching,
    searchQuery,
    setSearchQuery,
    filters,
    totalPages,
    totalResults,
    handleSearchChange,
    handlePageChange,
    updateUrlParams,
    setFilters,
    page, // Destructure 'page' from the hook
  } = usePropertySearch(initialFilters, 16); // Use the custom hook

  const { data: sliderSlides = [] } = useGetSliderQuery();
  const { data: categories = [] } = useGetCategoriesQuery();

  const { data: userData, isLoading: userLoading, error: userError, refetch: triggerGetUserData } = useGetUserDataQuery(undefined, {
    skip: !isAuthenticated || !!user,
  });

  const buildHeroSearchUrl = ({
    location = '',
    transactionType = 'all',
    categoryId = 'all',
    bedrooms = 'any',
    maxBudget = ''
  } = {}) => {
    const params = new URLSearchParams();

    if (location) {
      params.set('q', location);
      params.set('location', location);
    }
    if (transactionType && transactionType !== 'all') {
      params.set('type', transactionType);
    }
    if (categoryId && categoryId !== 'all') {
      params.set('category_id', categoryId);
    }
    if (bedrooms && bedrooms !== 'any') {
      params.set('bedrooms', bedrooms);
    }
    if (maxBudget) {
      const sanitizedBudget = String(maxBudget).replace(/[^0-9]/g, '');
      if (sanitizedBudget) params.set('priceRangeMax', sanitizedBudget);
    }

    const queryString = params.toString();
    return `/property-search-browse${queryString ? `?${queryString}` : ''}`;
  };

  const handleHeroSearch = ({
    location = searchQuery,
    transactionType = 'all',
    categoryId = 'all',
    bedrooms = 'any',
    maxBudget = ''
  } = {}) => {
    navigate(buildHeroSearchUrl({ location, transactionType, categoryId, bedrooms, maxBudget }));
  };

  const handleQuickSearch = (query) => {
    navigate(buildHeroSearchUrl({ location: query }));
  };

  useEffect(() => {
    if (isAuthenticated && !user) { // Only trigger if authenticated and user data is not already available
      triggerGetUserData();
    }
  }, [isAuthenticated, user, triggerGetUserData]);

  const searchContext = {
    location: filters?.location,
    propertyType: filters?.propertyType,
    priceRange: filters?.priceMin || filters?.priceMax ? 
      `${filters?.priceMin ? '$' + parseInt(filters?.priceMin)?.toLocaleString() : ''}${filters?.priceMin && filters?.priceMax ? ' - ' : ''}${filters?.priceMax ? '$' + parseInt(filters?.priceMax)?.toLocaleString() : ''}` : null,
    queryString: '' // The URL params are now managed by the hook
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        {/* Hero Slider */}
        <HeroSlider
          slides={sliderSlides}
          categories={categories}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery} // Use setSearchQuery from hook
          onSearch={handleHeroSearch}
          onQuickSearch={handleQuickSearch}
        />


        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          
          {/* Categories Slider */}
          <CategoriesSlider className ="py-10"
            categories={categories}
            onCategorySelect={(categoryId) => {
              // Always navigate to the browse page so the selected category is applied globally
              if (!categoryId || categoryId === 'all') {
                navigate('/property-search-browse');
              } else {
                const params = new URLSearchParams();
                params.set('category_id', categoryId);
                navigate(`/property-search-browse?${params.toString()}`);
              }
            }}
            selectedCategoryId={filters.propertyType || 'all'}
          />

          {/* Property Listings Heading */}
          <div className="mb-6 py-8">
            <h2 className="text-2xl  text-foreground">Property Listings</h2>
            <p className="text-muted-foreground mt-1">
              Discover your perfect property from our curated collection
            </p>
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            <PropertyGrid
              properties={properties}
              loading={isLoading || isFetching}
              favoritePropertyIds={favoritePropertyIds}
              currentPage={page}
              totalPages={totalPages}
              totalResults={totalResults}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PropertySearchBrowse;
